// =============================================================================
// joinHousehold.ts — redeem a member invite code to join an EXISTING household
// (v0.2.42)
//
// Extracted from the old Auth.tsx so both the signup page and the first-run
// Welcome screen can share one join path. Member codes (type 'parent' |
// 'applicant', or legacy codes with no type but a household_id) add the caller
// to that household. The old 'new_household' code type is no longer minted —
// creating a household no longer needs a code (see createHousehold.ts) — so if
// one is somehow entered here we send the user to the "create" path instead.
// =============================================================================
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase';

export async function joinViaInviteCode(opts: {
  uid: string;
  code: string;
  email: string;
  displayName: string;
}): Promise<{ householdId: string }> {
  if (!db) throw new Error('Firestore not configured');
  const codeStr = opts.code.trim().toUpperCase();
  if (!codeStr) throw new Error('Please enter an invite code.');

  const codeRef = doc(db, 'invite_codes', codeStr);
  const codeSnap = await getDoc(codeRef);
  if (!codeSnap.exists()) throw new Error('That invite code is not valid.');
  const codeData = codeSnap.data();
  if (codeData.used) throw new Error('This invite code has already been used.');

  // A 'new_household' code is for creating a household, not joining one.
  if (codeData.type === 'new_household') {
    throw new Error(
      'That code is for creating a new household — go back and choose "Create a new household."'
    );
  }

  const householdId = codeData.household_id as string;
  const role = (codeData.role as 'parent' | 'applicant') ?? 'parent';
  const applicantId = (codeData.applicant_id as string | null) ?? null;
  if (!householdId) {
    throw new Error('That invite code is not valid (no household attached).');
  }

  // Create the caller's member doc (rules allow self-create on invite accept).
  await setDoc(doc(db, 'households', householdId, 'members', opts.uid), {
    user_id: opts.uid,
    email: opts.email,
    display_name: opts.displayName,
    role,
    applicant_id: applicantId,
    invited_by: codeData.created_by ?? null,
    invited_at: codeData.created_at ?? serverTimestamp(),
    accepted_at: serverTimestamp(),
    status: 'active',
    last_active_at: serverTimestamp(),
    archived: false,
    email_preferences: { deadline_reminders: true },
  });

  // Append the caller to the household's member_uids.
  await updateDoc(doc(db, 'households', householdId), {
    member_uids: arrayUnion(opts.uid),
    updated_at: serverTimestamp(),
  });

  // If joining as an applicant, link that applicant doc to this user.
  if (role === 'applicant' && applicantId) {
    await updateDoc(doc(db, 'households', householdId, 'applicants', applicantId), {
      linked_user_id: opts.uid,
      updated_at: serverTimestamp(),
    });
  }

  // Mark the code used so it can't be redeemed again.
  await updateDoc(codeRef, {
    used: true,
    used_by: opts.uid,
    used_at: serverTimestamp(),
  });

  return { householdId };
}
